import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import AuditMetrics from './components/AuditMetrics';
import AuditsList from './components/AuditsList';
import QuickActionsToolbar from './components/QuickActionsToolbar';
import AuditFilters from './components/AuditFilters';
import CompletionTrendChart from './components/CompletionTrendChart';

const SafetyAuditDashboard = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data for audits
  const auditsData = [
    {
      id: 'AUD-001',
      title: 'Q4 2025 Workplace Safety Audit',
      description: 'Comprehensive workplace safety assessment covering all departments',
      status: 'active',
      location: 'Plant 1 - Main Facility',
      createdDate: '2025-11-15',
      publishDate: '2025-11-20',
      dueDate: '2025-12-31',
      totalQuestions: 45,
      responseRate: 78,
      completedUsers: 35,
      totalUsers: 45,
      averageScore: 87,
      createdBy: 'John Smith',
      questionBanks: ['General Safety', 'Fire Safety', 'Emergency Procedures']
    },
    {
      id: 'AUD-002',
      title: 'Chemical Handling Compliance Audit',
      description: 'Assessment of chemical storage and handling procedures',
      status: 'active',
      location: 'Plant 2 - North Wing',
      createdDate: '2025-11-10',
      publishDate: '2025-11-15',
      dueDate: '2025-12-20',
      totalQuestions: 32,
      responseRate: 92,
      completedUsers: 28,
      totalUsers: 30,
      averageScore: 91,
      createdBy: 'Sarah Johnson',
      questionBanks: ['Chemical Safety', 'PPE Requirements']
    },
    {
      id: 'AUD-003',
      title: 'Equipment Safety Inspection',
      description: 'Regular equipment safety checks and compliance verification',
      status: 'completed',
      location: 'Plant 3 - South Wing',
      createdDate: '2025-10-01',
      publishDate: '2025-10-05',
      dueDate: '2025-11-30',
      totalQuestions: 28,
      responseRate: 100,
      completedUsers: 20,
      totalUsers: 20,
      averageScore: 94,
      createdBy: 'Mike Chen',
      questionBanks: ['Equipment Safety', 'Maintenance Standards']
    },
    {
      id: 'AUD-004',
      title: 'Emergency Response Readiness',
      description: 'Evaluation of emergency response procedures and preparedness',
      status: 'draft',
      location: 'Warehouse 1',
      createdDate: '2025-12-01',
      publishDate: null,
      dueDate: '2025-12-30',
      totalQuestions: 38,
      responseRate: 0,
      completedUsers: 0,
      totalUsers: 25,
      averageScore: 0,
      createdBy: 'Emily Davis',
      questionBanks: ['Emergency Procedures', 'Evacuation Plans', 'First Aid']
    }
  ];

  const metricsData = {
    totalAudits: 15,
    activeAudits: 8,
    completedAudits: 7,
    averageCompletionRate: 85,
    pendingResponses: 127,
    totalParticipants: 340,
    averageScore: 88,
    overdueAudits: 2
  };

  // Filter audits based on selected filters
  const filteredAudits = auditsData?.filter(audit => {
    const locationMatch = selectedLocation === 'all' || audit?.location === selectedLocation;
    const statusMatch = selectedStatus === 'all' || audit?.status === selectedStatus;
    const searchMatch = !searchQuery || 
      audit?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      audit?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return locationMatch && statusMatch && searchMatch;
  });

  const handleCreateAudit = () => {
    navigate('/audit-survey-builder');
  };

  const handleViewAudit = (auditId) => {
    navigate(`/audit-response-tracker?audit=${auditId}`);
  };

  const handleEditAudit = (auditId) => {
    navigate(`/audit-survey-builder?edit=${auditId}`);
  };

  const handlePublishAudit = (auditId) => {
    console.log('Publishing audit:', auditId);
    // Handle publish logic
  };

  const handleDeleteAudit = (auditId) => {
    console.log('Deleting audit:', auditId);
    // Handle delete logic
  };

  const handleExportReports = () => {
    console.log('Exporting reports...');
  };

  const handleViewQuestionBanks = () => {
    console.log('Viewing question banks...');
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-foreground">Safety Audits</h1>
          <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            <span className="text-sm font-medium text-foreground">
              {metricsData?.activeAudits} Active Audits
            </span>
          </div>
        </div>
        <p className="text-muted-foreground">
          Create safety audit surveys, publish to users, track completion, and analyze responses with historical comparisons.
        </p>
      </div>
      {/* Quick Actions Toolbar */}
      <QuickActionsToolbar
        onCreateAudit={handleCreateAudit}
        onExportReports={handleExportReports}
        onViewQuestionBanks={handleViewQuestionBanks}
      />
      {/* Metrics Cards */}
      <AuditMetrics metrics={metricsData} />
      {/* Completion Trend Chart */}
      <div className="mb-6">
        <CompletionTrendChart />
      </div>
      {/* Filters and Search */}
      <AuditFilters
        selectedLocation={selectedLocation}
        onLocationChange={setSelectedLocation}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
      {/* Audits List */}
      <AuditsList
        audits={filteredAudits}
        onViewAudit={handleViewAudit}
        onEditAudit={handleEditAudit}
        onPublishAudit={handlePublishAudit}
        onDeleteAudit={handleDeleteAudit}
      />
      {/* Empty State */}
      {filteredAudits?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <Icon name="ClipboardCheck" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No audits found</h3>
          <p className="text-sm text-muted-foreground mb-6">
            {searchQuery || selectedLocation !== 'all' || selectedStatus !== 'all' ?'Try adjusting your filters to see more results.' :'Get started by creating your first safety audit survey.'}
          </p>
          <button
            onClick={handleCreateAudit}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            <Icon name="Plus" size={20} />
            Create New Audit
          </button>
        </div>
      )}
    </div>
  );
};

export default SafetyAuditDashboard;